import {
  ChequeGiroClearingLiteModel,
  useGetChequeGiroClearings,
} from 'api-hooks/transactions/cheque-giro-clearing';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { Container, SeparatorVertical, TopContainer } from 'components/styles';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import ChequeGiroClearingTable from './components/table';
export default function ChequeGiroClearingList() {
  const { t } = useTranslation();
  const { queryPage } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const formatDate = useDate();

  const _columns = React.useMemo<IColumn<ChequeGiroClearingLiteModel>[]>(
    () => [
      {
        header: t('common:cheque_or_giro'),
        accessorKey: 'chequeGiro',
        minSize: 130,
        sortName: 'name',
        cell: ({ row }) => {
          const { original } = row;
          return (
            <>{`${original.chequeGiro.name} - ${original.chequeGiro.bankCode}`}</>
          );
        },
      },
      {
        header: t('common:created_at'),
        accessorKey: 'createdAt',
        minSize: 150,
        maxSize: 150,
        cell: ({ getValue }) => (
          <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>
        ),
      },
      {
        header: t('common:reference'),
        accessorKey: 'referenceNumber',
        minSize: 100,
      },
      {
        header: t('common:type'),
        accessorKey: 'type',
        minSize: 100,
        sortName: 'type',
        cell: ({ getValue }) => <>{t(`common:${getValue() as string}`)}</>,
      },
      {
        header: t('common:status'),
        accessorKey: 'status',
        minSize: 100,
        sortName: 'status',
        cell: ({ getValue }) => <>{t(`common:${getValue() as string}`)}</>,
      },
      {
        header: t('common:issue_at'),
        accessorKey: 'issuedAt',
        minSize: 100,
        sortName: 'issued_at',
        cell: ({ getValue }) => (
          <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>
        ),
      },
      {
        header: t('common:available_at'),
        accessorKey: 'availableAt',
        minSize: 100,
        sortName: 'available_at',
        cell: ({ getValue }) => (
          <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>
        ),
      },
      {
        header: t('common:expiry_at'),
        accessorKey: 'expiredAt',
        minSize: 100,
        sortName: 'expired_at',
        cell: ({ getValue }) => (
          <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>
        ),
      },
      {
        header: t('common:bank_account'),
        accessorKey: 'bankAccount',
        minSize: 100,
        cell: ({ row }) => {
          const { original } = row;
          return (
            <>{`${original.bankAccount.code} - ${original.bankAccount.name}`}</>
          );
        },
      },
    ],
    [formatDate, t],
  );
  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetChequeGiroClearings,
    {
      params: {
        page,
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data?.sorts;
    }, _columns),
  );
  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error.message });
    }
  }, [error]);
  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:cheque_giro_clearing')}
          onRefresh={refetch}
          showAdd={false}
        />
        <SeparatorVertical $margin={8} />
        <TopContainer>
          <QueryFilter filters={filters} isUseResponseOptions />
        </TopContainer>
        <ChequeGiroClearingTable
          chequeGiroClearing={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
